import { useMemo } from 'react';
import type { ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, MessageBar, MessageBarBody, Spinner, Text, makeStyles, tokens } from '@fluentui/react-components';
import { AddRegular, ArrowUploadRegular } from '@fluentui/react-icons';
import { useClient, useConversions, useExtractionAccuracy, useJournals, useUploads } from '../services/conversionsApi';
import { usePageStyles } from '../components/layout/pageStyles';
import { useRouterNavigate } from '../components/links/useEntityLinkProps';
import { realLinkProps } from '../components/links/realLinkProps';
import { deriveWorkItems } from '../lib/workItems';
import { clientStreak, lastBankFile, overviewCounts } from '../lib/overview';
import { formatLocalDateTime } from '../lib/provenance';

const useStyles = makeStyles({
  actions: { display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  tiles: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: tokens.spacingHorizontalL,
  },
  tile: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  tileLabel: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase300 },
  // A step BELOW the page title (the polish pass, P3: one title per page, at one ramp step).
  tileValue: { fontSize: tokens.fontSizeBase600, lineHeight: tokens.lineHeightBase600, fontWeight: tokens.fontWeightSemibold },
  failed: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS, flexWrap: 'wrap' },
  tileDetail: { color: tokens.colorNeutralForeground2, fontSize: tokens.fontSizeBase300 },
  tileLink: { marginTop: 'auto', paddingTop: tokens.spacingVerticalS, fontSize: tokens.fontSizeBase300 },
});

/**
 * Overview: the client's DASHBOARD (Christian's walk of deploy #20; it was a placeholder status box). Scoped to the
 * header pick, like Accuracy: work waiting for a journal, journals awaiting export or Xero confirmation, the last bank
 * file, the client's DR-15 streak, and the two things a bookkeeper starts most (New journal, Upload statement).
 *
 * <p>Every count is read off the client's Work (`deriveWorkItems`, `lib/overview`), so this page and the Work tab never
 * disagree, and each tile opens the list it counts. A failed list says so on its tile; it never reads as zero.</p>
 */
export default function LandingPage() {
  const styles = useStyles();
  const frame = usePageStyles();
  const { t, i18n } = useTranslation();
  // The quick actions are real links (fleet ruling 2026-10-29): right-click opens them in a new tab.
  const navigate = useRouterNavigate();
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get('clientId') ?? '';

  const client = useClient(clientId);
  const journals = useJournals(clientId);
  // Removed uploads too, the same query the Work tab reads: a journal still names a source removed after it was made.
  const uploads = useUploads(clientId, true);
  const conversions = useConversions(clientId);
  const accuracy = useExtractionAccuracy(clientId);

  const items = useMemo(
    () =>
      journals.data
        ? deriveWorkItems({ clientId, journals: journals.data, uploads: uploads.data ?? [], conversions: conversions.data ?? [] })
        : [],
    [clientId, journals.data, uploads.data, conversions.data],
  );
  const counts = overviewCounts(items);
  const bankFile = conversions.data ? lastBankFile(conversions.data) : null;
  const streak = clientStreak(accuracy.data?.byClient, clientId);

  const q = (extra: Record<string, string> = {}) => new URLSearchParams({ clientId, ...extra }).toString();
  const work = (status?: string) => `/clients?${q({ tab: 'work', ...(status ? { status } : {}) })}`;

  if (!clientId) {
    return (
      <div className={frame.page}>
        <div className={frame.header}>
          <Text as="h1" className={frame.title}>{t('landingPage.title')}</Text>
          <Text className={frame.subtitle}>{t('landingPage.subtitle')}</Text>
        </div>
        <MessageBar intent="info">
          <MessageBarBody>{t('landingPage.pickClient')}</MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  /**
   * A tile's figure: a spinner while its list loads; "Couldn't load" with Retry when it failed, never a zero (the polish
   * pass, P6); and a zero reads as a sentence, never a bare "0" (P5).
   */
  const value = (
    query: { isLoading: boolean; isError: boolean; refetch: () => unknown },
    n: ReactNode,
    none?: string,
  ) =>
    query.isLoading ? (
      <Spinner size="tiny" />
    ) : query.isError ? (
      <span className={styles.failed}>
        <Text className={styles.tileDetail}>{t('landingPage.loadFailed')}</Text>
        <Button size="small" appearance="transparent" onClick={() => void query.refetch()}>{t('landingPage.retry')}</Button>
      </span>
    ) : n === 0 && none ? (
      none
    ) : (
      n
    );
  const workQuery = journals;

  return (
    <div className={frame.page}>
      <div className={frame.header}>
        <Text as="h1" className={frame.title}>{client.data?.name ?? t('landingPage.thisClient')}</Text>
        <Text className={frame.subtitle}>{t('landingPage.clientSubtitle')}</Text>
      </div>

      <div className={styles.actions}>
        <Button as="a" appearance="primary" icon={<AddRegular />} {...realLinkProps(`/journals?${q({ new: '1' })}`, { navigate })}>
          {t('landingPage.newJournal')}
        </Button>
        <Button as="a" icon={<ArrowUploadRegular />} {...realLinkProps(`/bank-transactions?${q()}`, { navigate })}>
          {t('landingPage.uploadStatement')}
        </Button>
      </div>

      <div className={styles.tiles}>
        <section className={`card ${styles.tile}`} aria-label={t('landingPage.tiles.toJournal')}>
          <Text className={styles.tileLabel}>{t('landingPage.tiles.toJournal')}</Text>
          <Text className={styles.tileValue}>{value(workQuery, counts.toJournal, t('landingPage.tiles.toJournalNone'))}</Text>
          <Text className={styles.tileDetail}>{t('landingPage.tiles.toJournalDetail')}</Text>
          <Link className={styles.tileLink} to={work('uploaded')}>{t('landingPage.tiles.openWork')}</Link>
        </section>

        <section className={`card ${styles.tile}`} aria-label={t('landingPage.tiles.awaitingExport')}>
          <Text className={styles.tileLabel}>{t('landingPage.tiles.awaitingExport')}</Text>
          <Text className={styles.tileValue}>{value(workQuery, counts.awaitingExport, t('landingPage.tiles.awaitingExportNone'))}</Text>
          <Text className={styles.tileDetail}>{t('landingPage.tiles.awaitingExportDetail')}</Text>
          <Link className={styles.tileLink} to={`/journals?${q()}`}>{t('landingPage.tiles.openJournals')}</Link>
        </section>

        <section className={`card ${styles.tile}`} aria-label={t('landingPage.tiles.awaitingConfirmation')}>
          <Text className={styles.tileLabel}>{t('landingPage.tiles.awaitingConfirmation')}</Text>
          <Text className={styles.tileValue}>{value(workQuery, counts.awaitingConfirmation, t('landingPage.tiles.awaitingConfirmationNone'))}</Text>
          <Text className={styles.tileDetail}>{t('landingPage.tiles.awaitingConfirmationDetail')}</Text>
          <Link className={styles.tileLink} to={work('unconfirmed')}>{t('landingPage.tiles.openWork')}</Link>
        </section>

        <section className={`card ${styles.tile}`} aria-label={t('landingPage.tiles.lastBankFile')}>
          <Text className={styles.tileLabel}>{t('landingPage.tiles.lastBankFile')}</Text>
          <Text className={styles.tileValue}>
            {value(
              conversions,
              bankFile ? formatLocalDateTime(bankFile.generatedAt, i18n.language) : t('landingPage.tiles.noBankFile'),
            )}
          </Text>
          {bankFile && <Text className={styles.tileDetail}>{bankFile.fileName}</Text>}
          <Link className={styles.tileLink} to={`/bank-transactions?${q()}`}>{t('landingPage.tiles.openBank')}</Link>
        </section>

        <section className={`card ${styles.tile}`} aria-label={t('landingPage.tiles.streak')}>
          <Text className={styles.tileLabel}>{t('landingPage.tiles.streak')}</Text>
          <Text className={styles.tileValue}>
            {value(accuracy, streak ? streak.streak : t('landingPage.tiles.notGraded'))}
          </Text>
          <Text className={styles.tileDetail}>
            {streak ? t('landingPage.tiles.streakDetail', { graded: streak.graded }) : t('landingPage.tiles.notGradedDetail')}
          </Text>
          <Link className={styles.tileLink} to={`/extraction-accuracy?${q()}`}>{t('landingPage.tiles.openAccuracy')}</Link>
        </section>
      </div>
    </div>
  );
}
